import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Menu } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const Header = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { t, getLocalizedPath } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isActive = (path: string) => {
    // Get path segments and handle language prefix
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const currentPath =
      pathSegments[0] === "en" || pathSegments[0] === "de"
        ? "/" + pathSegments.slice(1).join("/")
        : location.pathname;

    return currentPath === path;
  };

  const navigationLinks = [
    { path: "/transactions", label: t("common.transactions") },
    { path: "/projects", label: t("common.projects") },
    { path: "/organizations", label: t("common.organizations") },
  ];

  const handleNavLinkClick = () => {
    // Close the menu when a navigation link is clicked
    setIsMenuOpen(false);
  };

  const renderNavigationLinks = () => (
    <>
      {navigationLinks.map((link) => (
        <NavigationMenuItem key={link.path} className="relative group/nav-item">
          {/* Animated dot indicator - only shows on active or individual hover */}
          <div
            className={cn(
              "absolute left-4 top-1/2 w-1.5 h-1.5 rounded-full bg-sky-500 -translate-y-1/2 -translate-x-4 transition-all duration-300 ease-in-out",
              isActive(link.path)
                ? "opacity-100 scale-100"
                : "opacity-0 scale-0 group-hover/nav-item:opacity-100 group-hover/nav-item:scale-100",
            )}
          />
          <Link
            to={getLocalizedPath(link.path)}
            className={navigationMenuTriggerStyle()}
          >
            {link.label}
          </Link>
        </NavigationMenuItem>
      ))}
    </>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white">
      <div className="container mx-auto flex h-20 items-center px-4">
        {/* Logo on the left */}
        <div className="flex items-center">
          <Link to={getLocalizedPath("/")} className="flex items-center gap-2">
            <img
              src="https://cdn.tree.ly/assets/v4/registry-app/registry_logo.svg"
              alt={t("header.titleRegistry")}
              className="h-10"
            />
          </Link>
        </div>

        {/* Center-aligned navigation - absolutely positioned */}
        {!isMobile && (
          <div className="absolute left-0 right-0 mx-auto flex justify-center w-full pointer-events-none">
            <NavigationMenu className="pointer-events-auto">
              <NavigationMenuList>{renderNavigationLinks()}</NavigationMenuList>
            </NavigationMenu>
          </div>
        )}

        {/* Language switcher and mobile menu button on the right */}
        <div className="flex items-center gap-2 ml-auto">
          <LanguageSwitcher />

          {/* Mobile menu button */}
          {isMobile && (
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[80vw] sm:w-[350px]">
                <div className="flex flex-col space-y-4 py-4">
                  <div className="px-3 py-2">
                    <h2 className="mb-2 text-lg font-semibold">Menu</h2>
                  </div>
                  <nav className="flex flex-col space-y-2">
                    {navigationLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={getLocalizedPath(link.path)}
                        className={cn(
                          "px-3 py-2 text-sm font-medium relative group/mobile-nav-item transition-colors",
                          isActive(link.path)
                            ? "text-silvaconsult"
                            : "text-gray-700 hover:text-silvaconsult",
                        )}
                        onClick={handleNavLinkClick}
                      >
                        {/* Mobile dot indicator */}
                        <div
                          className={cn(
                            "absolute left-0 top-1/2 w-1.5 h-1.5 rounded-full bg-sky-500 -translate-y-1/2 transition-all duration-300 ease-in-out",
                            isActive(link.path)
                              ? "opacity-100 scale-100 -translate-x-3"
                              : "opacity-0 scale-0 group-hover/mobile-nav-item:opacity-100 group-hover/mobile-nav-item:scale-100 -translate-x-1",
                          )}
                        />
                        {link.label}
                      </Link>
                    ))}
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
